/* AI policy.
 *
 * A pure function from game state to one action. It answers `G.pending`
 * requests through the same interface a human does, so it cannot silently skip
 * a decision the player has to make.
 *
 * Three faults in the prototype's AI are fixed here, all of which made
 * simulated win-rates misleading rather than merely weak:
 *   1. It never played Events, so removal-heavy decks played a card short.
 *   2. It attached every DON!! to one attacker, leaving nothing to pay for a
 *      [Counter] event on the opponent's turn — it could never defend.
 *   3. Its triggers never resolved, so it played a strictly worse game than
 *      the human on the other side of the table.
 */

import {
  legalActions, apply, other, charPower, leaderPower, findChar,
  attackTargets, canAttack, costOf, counterOptions, view,
} from './engine.js';
import { parseEffects, keywords, counterValue } from './cardtext.js';
import { scriptFor } from './scripts.js';

/**
 * The activation cost the engine will actually charge for a card.
 *
 * Mirrors `enqueueEffects`: a hand-written script wins over the text parser.
 * The AI reading its costs from a different source than the engine charges
 * them from is a bug waiting for the first scripted cost the parser cannot
 * read — a script exists precisely because a card's wording defeats the
 * parser, so that day is coming.
 *
 * Note this is defensive, not a live fix. The three scripted `trashSelf` cards
 * in current meta lists — OP15-032 Brook, OP16-098 Yamato, OP16-084 Kouzuki
 * Momonosuke — all say "You may trash this Character" in wording the parser
 * reads correctly, so today both sources agree and behaviour is unchanged.
 */
export function activateCost(c) {
  if (!c) return null;
  const scripted = scriptFor(c.id, 'activate');
  if (scripted) return scripted.cost ?? null;
  return parseEffects(c).find(e => e.when === 'activate')?.cost ?? null;
}

export const SKILL = { casual: 1, solid: 2, sharp: 3 };

/**
 * The behaviours a skill level actually turns on.
 *
 * These used to be bare `skill >= 3` comparisons scattered through the file,
 * which made the tiers impossible to take apart: `sharp` differs from `solid`
 * in three separate ways, and nothing could measure one of them without the
 * other two. The benchmark added in #57 then showed `sharp` losing to `solid`
 * (#56), and there was no way to ask which of the three was responsible.
 *
 * Naming them makes each one addressable, and lets a duel change exactly one.
 * The mapping below reproduces the old behaviour exactly.
 */
export function traitsFor(skill = SKILL.solid) {
  return {
    skill,
    /** Active DON!! held back to pay for a [Counter] on the opponent's turn. */
    counterReserveMax: skill < 2 ? 0 : (skill >= 3 ? 2 : 1),
    /**
     * How valuable a Character must be before counters are spent to save it,
     * on `cardValue`'s scale (a vanilla 5000 body is 5; a 7000 Blocker ~9.2).
     *
     * Infinity means "never defend a Character", which is what `solid` does.
     * A flat `true` here was measured as the sole cause of `sharp` losing to
     * `solid` (#56): defending every body, however cheap, spends counter cards
     * that were worth more held for the Leader.
     */
    defendCharacterMinValue: skill >= 3 ? 8 : Infinity,
    /** Counter a hit to the face while life is still comfortable (> 3). */
    defendAtHighLife: skill >= 3,
  };
}

/** A named variant of a policy — one trait changed, everything else held. */
export const withTraits = (skill, overrides) => ({ ...traitsFor(skill), ...overrides });

/* Accept either a skill number or an already-built traits object, so every
 * existing `decide(G, SKILL.solid)` call site keeps working unchanged. */
const asTraits = p => (typeof p === 'object' && p !== null ? p : traitsFor(p));

/* How much a card is worth deploying. Cheap heuristics, but tuned so the AI
 * doesn't dump its hand for no board impact. */
function cardValue(G, id) {
  const c = G.cards[id];
  if (!c) return 0;
  let v = (c.power || 0) / 1000;
  const kw = keywords(c);
  const fx = parseEffects(c);

  if (kw.blocker) v += 2.2;
  if (kw.rush) v += 1.6;
  /* [Rush: Character] scored zero — the same as no keyword at all — which
   * undervalued every card that prints it. It is worth real tempo and less
   * than plain Rush: it can only remove a rested body, never take Life, so
   * it does not close a game the turn it lands. */
  else if (kw.rushChar) v += 0.9;
  if (kw.doubleAttack) v += 1.5;
  if (c.counter) v += c.counter / 2000;

  for (const e of fx) {
    if (e.when !== 'onPlay') continue;
    for (const op of e.ops) {
      if (op.op === 'ko') v += 3.2;
      if (op.op === 'draw') v += 1.4 * op.n;
      if (op.op === 'dig') v += 1.1;
      if (op.op === 'power') v += 1.0;
      if (op.op === 'donAdd') v += 1.8 * op.n;
      if (op.op === 'playFree') v += 2.0;
      if (op.op === 'bounce') v += 2.4;
    }
  }
  // Events trade themselves for their effect, so they need to earn the slot.
  if (c.category === 'Event') v = Math.max(0, v - 0.5);
  if (c.category === 'Stage') v -= 1;
  return v;
}

/* Keep enough active DON!! to counter with on the opponent's turn. */
function counterReserve(G, s, t) {
  if (!t.counterReserveMax) return 0;
  const S = G[s];
  const playableCounterEvents = S.hand.filter(id => {
    const c = G.cards[id];
    return c && !c.counter && counterValue(c) > 0;
  });
  if (!playableCounterEvents.length) return 0;
  const cheapest = Math.min(...playableCounterEvents.map(id => costOf(G.cards[id])));
  return Math.min(t.counterReserveMax, cheapest);
}

/* ---------------------------------------------------------------------- */
/* pending answers                                                         */
/* ---------------------------------------------------------------------- */

function answerPending(G, p, t) {
  const s = p.side;
  const S = G[s];

  if (p.type === 'block') {
    // Block when the hit actually matters: low life, or the blocker survives.
    const atk = G.combat?.power ?? 0;
    const goingToFace = G.combat?.target.kind === 'leader';
    if (t.skill < 2 || !goingToFace) return { type: 'block', uid: null };
    const worth = S.life.length <= 2;
    if (!worth) return { type: 'block', uid: null };
    const survivors = p.options.filter(o => o.power > atk);
    const pick = survivors[0] || p.options[0];
    return { type: 'block', uid: pick ? pick.uid : null };
  }

  if (p.type === 'counter') {
    const need = p.attackPower - p.defendPower + 1;
    if (need <= 0) return { type: 'counter', index: null };
    const toFace = G.combat?.target.kind === 'leader';

    /* Don't burn cards defending a body that isn't worth them.
     *
     * This used to be all-or-nothing, and "defend everything" measured worse
     * than "defend nothing" (#56) — counters spent saving a 2-cost body are
     * counters not available for the Leader, and the body is replaceable while
     * life is not. Judging the specific Character rather than the situation is
     * what makes defending them a gain instead of a leak. */
    if (!toFace) {
      const uid = G.combat?.target?.uid;
      const ch = uid ? findChar(G, s, uid) : null;
      const worth = ch ? cardValue(G, ch.id) : 0;
      if (worth < t.defendCharacterMinValue) return { type: 'counter', index: null };
    }
    if (toFace && S.life.length > 3 && !t.defendAtHighLife) return { type: 'counter', index: null };

    // Only commit if the total available actually gets there.
    const affordable = p.options.filter(o => o.cost <= S.donActive);
    const reachable = affordable.reduce((a, o) => a + o.value, 0);
    if (reachable < need) return { type: 'counter', index: null };

    // Spend the smallest counter that still contributes.
    const sorted = affordable.slice().sort((a, b) => a.value - b.value);
    const exact = sorted.find(o => o.value >= need);
    return { type: 'counter', index: (exact || sorted[0]).index };
  }

  if (p.type === 'trigger') {
    return { type: 'trigger', activate: t.skill >= 2 };
  }

  if (p.type === 'confirm') {
    // Pay optional costs when the payoff is a real effect and the hand can spare it.
    return { type: 'choose', value: t.skill >= 2 && S.hand.length > 2 };
  }

  /* "Rest any number of DON!! for +N power each" — a quantity, not a menu.
   *
   * Scored before the generic `mode` branch because that one reads a mode's
   * ops and every option here would tie at zero, so the AI would always take
   * option 0 and never use the ability at all. A Leader whose whole plan is
   * this conversion would then simulate as if it had no text.
   *
   * Policy: spend only what the battle needs. Resting DON!! costs next turn's
   * board development, so paying 3 to survive a hit worth 1 is a real loss —
   * and resting for a hit that lands anyway is the worst outcome of all. */
  if (p.type === 'mode' && p.defensiveBonus) {
    const per = p.defensiveBonus;
    const max = p.options.length - 1;
    if (t.skill < 2 || max <= 0) return { type: 'choose', value: 0 };

    const gap = (p.attackPower ?? 0) - (p.defendPower ?? 0);
    if (gap < 0) return { type: 'choose', value: 0 };      // already surviving

    const toFace = G.combat?.target.kind === 'leader';
    // Don't burn the DON!! defending a body, or a face hit that life can absorb.
    // Same worth test as the counter step — resting DON!! is a real cost too.
    if (!toFace) {
      const uid = G.combat?.target?.uid;
      const ch = uid ? findChar(G, s, uid) : null;
      const worth = ch ? cardValue(G, ch.id) : 0;
      if (worth < t.defendCharacterMinValue) return { type: 'choose', value: 0 };
    }
    if (toFace && S.life.length > 3 && !t.defendAtHighLife) return { type: 'choose', value: 0 };

    const need = Math.ceil((gap + 1) / per);
    // If it can't get there, keep the DON!! rather than paying for nothing.
    return { type: 'choose', value: need <= max ? need : 0 };
  }

  if (p.type === 'mode') {
    // "Choose one" — prefer the mode that draws cards or develops the board.
    const score = m => {
      let v = 0;
      for (const op of m.ops || []) {
        if (op.op === 'draw') v += 2 * (op.n || 1);
        if (op.op === 'playFree' || op.op === 'playFromTrash') v += 3;
        if (op.op === 'gainKeyword') v += 1.5;
        if (op.op === 'returnStage') v += S.chars.length ? 0.5 : 0;
      }
      return v;
    };
    const best = p.options.slice().sort((a, b) => score(p.modes[b.index]) - score(p.modes[a.index]))[0];
    return { type: 'choose', value: best.index };
  }

  if (p.type === 'manual') {
    const prefer = ['draw1', 'donActive', 'plus1000', 'minus1000', 'life', 'mill', 'skip'];
    const opts = p.options.map(o => o.key);
    const pick = prefer.find(k => opts.includes(k)) || 'skip';
    return { type: 'choose', value: pick };
  }

  if (p.type === 'target') {
    const opts = p.options;
    if (!opts.length) return { type: 'choose', value: null };

    if (p.kind === 'ko' || p.kind === 'bounce') {
      // Remove the biggest threat we're allowed to touch.
      const d = other(s);
      const best = opts.slice().sort((a, b) => {
        const ca = findChar(G, d, a.uid), cb = findChar(G, d, b.uid);
        return (cb ? charPower(G, d, cb) : 0) - (ca ? charPower(G, d, ca) : 0);
      })[0];
      return { type: 'choose', value: best.uid };
    }
    if (p.kind === 'power') {
      const t = p.targetSide;
      const powerOf = o => {
        const c = findChar(G, t, o.uid);
        return c ? charPower(G, t, c) : 0;
      };
      /* Both branches of this used to return `pb - pa`, so the conditional did
       * nothing and a buff was aimed like a debuff. They want opposite things:
       * a debuff should hit the biggest threat, while a buff is wasted on a
       * body that cannot swing this turn however large it is. */
      const sorted = opts.slice().sort((a, b) => {
        if (p.amount < 0) return powerOf(b) - powerOf(a);
        const liveA = canAttack(G, t, a.uid) ? 1 : 0;
        const liveB = canAttack(G, t, b.uid) ? 1 : 0;
        return (liveB - liveA) || (powerOf(b) - powerOf(a));
      });
      return { type: 'choose', value: sorted[0].uid };
    }
    if (p.kind === 'playFree') {
      const best = opts.slice().sort((a, b) => cardValue(G, G[s].hand[b.index]) - cardValue(G, G[s].hand[a.index]))[0];
      return { type: 'choose', value: best.index };
    }
    if (p.kind === 'donGive') {
      // Load the body that can actually swing this turn, else the biggest.
      const live = opts.filter(o => canAttack(G, s, o.uid));
      const pool = live.length ? live : opts;
      const best = pool.slice().sort((a, b) => {
        const ca = findChar(G, s, a.uid), cb = findChar(G, s, b.uid);
        return (cb ? charPower(G, s, cb) : 0) - (ca ? charPower(G, s, ca) : 0);
      })[0];
      return { type: 'choose', value: best.uid };
    }
    if (p.kind === 'rest') return { type: 'choose', value: opts[0].uid };

    // Disable / remove / copy: always aim at the opponent's biggest body.
    if (['flag', 'costMod', 'basePowerFrom', 'returnToHand', 'bottomDeck'].includes(p.kind)) {
      const t = p.targetSide || other(s);
      const owners = p.owners;
      const ranked = opts.slice().sort((a, b) => {
        const sideOf = u => (owners ? owners[u] : t);
        const ca = findChar(G, sideOf(a.uid), a.uid);
        const cb = findChar(G, sideOf(b.uid), b.uid);
        const pa = ca ? charPower(G, sideOf(a.uid), ca) : 0;
        const pb = cb ? charPower(G, sideOf(b.uid), cb) : 0;
        // Never bounce or debuff our own board when a foe is available.
        const mineA = owners && owners[a.uid] === s ? -1e6 : 0;
        const mineB = owners && owners[b.uid] === s ? -1e6 : 0;
        return (pb + mineB) - (pa + mineA);
      });
      const pick = ranked[0];
      if (owners && owners[pick.uid] === s && p.optional) return { type: 'choose', value: null };
      return { type: 'choose', value: pick.uid };
    }

    // Recursion and Life manipulation: take the most expensive card available.
    if (['playFromTrash', 'searchTrashToHand', 'lifeFromTrash'].includes(p.kind)) {
      const best = opts.slice().sort((a, b) => cardValue(G, S.trash[b.index]) - cardValue(G, S.trash[a.index]))[0];
      return { type: 'choose', value: best.index };
    }
    if (p.kind === 'lifeFromHand') {
      // Bury the least useful card in hand.
      const worst = opts.slice().sort((a, b) => cardValue(G, S.hand[a.index]) - cardValue(G, S.hand[b.index]))[0];
      return { type: 'choose', value: worst.index };
    }
    if (p.kind === 'setActive') {
      const best = opts.slice().sort((a, b) => {
        const ca = findChar(G, s, a.uid), cb = findChar(G, s, b.uid);
        return (cb ? charPower(G, s, cb) : 0) - (ca ? charPower(G, s, ca) : 0);
      })[0];
      return { type: 'choose', value: best.uid };
    }

    return { type: 'choose', value: opts[0].uid ?? opts[0].index };
  }

  // Unknown request — take the first legal answer rather than deadlocking.
  const legal = legalActions(G);
  return legal[0] || { type: 'advance' };
}

/* ---------------------------------------------------------------------- */
/* main-phase policy                                                       */
/* ---------------------------------------------------------------------- */

function mainAction(G, s, t) {
  const S = G[s];
  const d = other(s);
  const D = G[d];

  // 1. Leader ability first — ramp compounds.
  const legal = legalActions(G);
  if (legal.some(a => a.type === 'leaderAbility')) return { type: 'leaderAbility' };

  /* 1b. Character activated abilities. Skip the ones whose cost removes the
   * body from the board while it could still attack — trashing a live attacker
   * for value is usually worse than swinging with it. */
  const activations = legal.filter(a => a.type === 'activateChar');
  for (const a of activations) {
    const ch = findChar(G, s, a.uid);
    const c = ch && G.cards[ch.id];
    const sacrifices = activateCost(c)?.trashSelf;
    if (sacrifices && canAttack(G, s, a.uid) && charPower(G, s, ch) >= 5000) continue;
    return a;
  }

  // 2. Deploy the best card we can still afford, keeping a counter reserve.
  const reserve = counterReserve(G, s, t);
  const plays = legal.filter(a => a.type === 'play');
  if (plays.length) {
    const scored = plays.map(a => {
      const id = S.hand[a.index];
      const c = G.cards[id];
      const leftAfter = S.donActive - costOf(c);
      return { a, id, v: cardValue(G, id), leftAfter };
    })
      // Respect the reserve unless the play is clearly strong.
      .filter(x => x.leftAfter >= reserve || x.v >= 6)
      .sort((x, y) => y.v - x.v);
    if (scored.length && scored[0].v > 0.5) return scored[0].a;
  }

  // 3. Attack. Prefer trades we win; otherwise go to the Leader's face.
  const attacks = legal.filter(a => a.type === 'attack');
  if (attacks.length) {
    const scored = attacks.map(a => {
      const power = a.attacker === 'leader' ? leaderPower(G, s) : charPower(G, s, findChar(G, s, a.attacker));
      if (a.target.kind === 'leader') {
        const lp = leaderPower(G, d);
        return { a, score: power >= lp ? 10 : -1 };
      }
      const ch = findChar(G, d, a.target.uid);
      if (!ch) return { a, score: -5 };
      const cp = charPower(G, d, ch);
      // Killing a rested body is worth roughly its power, if we can beat it.
      return { a, score: power >= cp ? 4 + cp / 2000 : -2 };
    }).sort((x, y) => y.score - x.score);

    if (scored[0].score > 0) {
      // Casual play doesn't hunt for trades.
      if (t.skill < 2 && scored[0].a.target.kind === 'char') {
        const face = scored.find(x => x.a.target.kind === 'leader' && x.score > 0);
        if (face) return face.a;
      }
      return scored[0].a;
    }
  }

  // 4. Spend leftover DON!! on power, keeping the counter reserve back.
  const attach = legal.filter(a => a.type === 'attach');
  if (attach.length && S.donActive > reserve) {
    // Feed the biggest attacker that can still swing this turn, else the Leader.
    const live = S.chars.filter(c => canAttack(G, s, c.uid));
    if (live.length) {
      const best = live.slice().sort((a, b) => charPower(G, s, b) - charPower(G, s, a))[0];
      return { type: 'attach', target: best.uid };
    }
    if (t.skill > 1 && !S.leaderRested) return { type: 'attach', target: 'leader' };
  }

  return { type: 'advance' };
}

/**
 * One action for whichever side the AI controls right now.
 *
 * `policy` is a SKILL number or a traits object from `traitsFor`/`withTraits`.
 */
export function decide(G, policy = SKILL.solid) {
  if (G.over) return null;
  const t = asTraits(policy);
  if (G.pending) return answerPending(G, G.pending, t);
  if (G.phase !== 'main') return { type: 'advance' };
  return mainAction(G, G.active, t);
}

/**
 * Drive the AI until control returns to `human` (or the game ends).
 * `guard` stops a malformed state from spinning forever.
 */
export function runAI(G, aiSide, policy = SKILL.solid, guard = 400) {
  const t = asTraits(policy);
  let n = 0;
  while (!G.over && n++ < guard) {
    const needsAI = G.pending ? G.pending.side === aiSide : G.active === aiSide;
    if (!needsAI) break;
    const action = decide(G, t);
    if (!action) break;
    apply(G, action);
  }
  return G;
}
